import { useState, useEffect } from "react";
import { getTopCrypto, getCryptoHoldings } from "@/lib/api";
import CryptoHoldingsPanel, { fmtCryptoPrice } from "@/components/CryptoHoldingsPanel";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { TrendingUp, TrendingDown, Coins } from "lucide-react";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { LineChart, Line, ResponsiveContainer } from "recharts";

function MiniSparkline({ data, positive }) {
  if (!data || data.length === 0) return null;
  const sampled = data.filter((_, i) => i % Math.max(1, Math.floor(data.length / 30)) === 0);
  const chartData = sampled.map((v, i) => ({ i, v }));
  return (
    <ResponsiveContainer width={80} height={28}>
      <LineChart data={chartData}>
        <Line type="monotone" dataKey="v" stroke={positive ? "rgb(var(--bt-positive))" : "rgb(var(--bt-negative))"} strokeWidth={1.2} dot={false} />
      </LineChart>
    </ResponsiveContainer>
  );
}

const HeldBadge = ({ sources, symbol }) => (sources ? (
  <span className="ml-2 inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] font-mono border border-bt-gold/40 text-bt-gold bg-bt-gold/10"
    title={[...sources].map(s => (s === "woodrow" ? "Woodrow" : "My Picks")).join(" + ")} data-testid={`crypto-held-${symbol}`}>
    Held {[...sources].map(s => (s === "woodrow" ? "W" : "M")).sort().join("·")}
  </span>
) : null);

export default function CryptoView() {
  const [cryptos, setCryptos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [holdings, setHoldings] = useState(null);

  useEffect(() => {
    getTopCrypto().then(r => { setCryptos(r.data || []); setLoading(false); }).catch(() => setLoading(false));
    getCryptoHoldings().then(r => setHoldings(r.data)).catch(() => setHoldings(null));
  }, []);

  const heldBy = {};
  (holdings?.holdings || []).forEach(h => { const k = h.symbol.toLowerCase(); (heldBy[k] = heldBy[k] || new Set()).add(h.source); });
  const images = Object.fromEntries(cryptos.map(c => [c.symbol, c.image]));
  const extraHeld = (holdings?.holdings || [])
    .filter((h, i, arr) => !cryptos.some(c => c.symbol === h.symbol.toLowerCase()) && arr.findIndex(x => x.symbol === h.symbol) === i);

  const totalMcap = cryptos.reduce((s, c) => s + (c.market_cap || 0), 0);

  return (
    <div className="max-w-[1600px] mx-auto px-3 sm:px-4 md:px-8 py-4 sm:py-6" data-testid="crypto-page">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl sm:text-4xl lg:text-5xl font-heading font-semibold tracking-tight text-bt-text-primary">
          Cryptocurrency
        </h1>
        <p className="text-base text-bt-text-secondary font-body mt-2">
          Live crypto market data powered by CoinGecko
        </p>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
        <div className="bg-bt-surface border border-bt-border-subtle p-6" data-testid="crypto-total-mcap">
          <p className="text-xs font-mono uppercase tracking-[0.2em] text-bt-text-secondary mb-2">Total Market Cap</p>
          <p className="text-3xl font-mono font-medium tracking-tighter text-bt-text-primary">
            {totalMcap > 0 ? `$${(totalMcap / 1e12).toFixed(2)}T` : "—"}
          </p>
        </div>
        <div className="bg-bt-surface border border-bt-border-subtle p-6" data-testid="crypto-count">
          <p className="text-xs font-mono uppercase tracking-[0.2em] text-bt-text-secondary mb-2">Tracked Coins</p>
          <p className="text-3xl font-mono font-medium tracking-tighter text-bt-text-primary">{cryptos.length}</p>
        </div>
        <div className="bg-bt-surface border border-bt-border-subtle p-6" data-testid="crypto-btc-dom">
          <p className="text-xs font-mono uppercase tracking-[0.2em] text-bt-text-secondary mb-2">BTC Dominance</p>
          <p className="text-3xl font-mono font-medium tracking-tighter text-bt-text-primary">
            {totalMcap > 0 ? `${((cryptos.find(c => c.symbol === "btc")?.market_cap || 0) / totalMcap * 100).toFixed(1)}%` : "—"}
          </p>
        </div>
      </div>

      <CryptoHoldingsPanel data={holdings} images={images} />

      {/* Crypto Table */}
      <div className="bg-bt-surface border border-bt-border-subtle" data-testid="crypto-table">
        {loading ? (
          <div className="p-6 space-y-3">
            {Array.from({ length: 8 }).map((_, i) => (
              <Skeleton key={i} className="h-12 w-full bg-bt-surface-2" />
            ))}
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow className="border-bt-border-subtle hover:bg-transparent">
                <TableHead className="text-bt-text-tertiary font-mono text-xs uppercase tracking-wider">#</TableHead>
                <TableHead className="text-bt-text-tertiary font-mono text-xs uppercase tracking-wider">Coin</TableHead>
                <TableHead className="text-bt-text-tertiary font-mono text-xs uppercase tracking-wider text-right">Price</TableHead>
                <TableHead className="text-bt-text-tertiary font-mono text-xs uppercase tracking-wider text-right">24h</TableHead>
                <TableHead className="text-bt-text-tertiary font-mono text-xs uppercase tracking-wider text-right hidden md:table-cell">Market Cap</TableHead>
                <TableHead className="text-bt-text-tertiary font-mono text-xs uppercase tracking-wider text-right hidden lg:table-cell">Volume</TableHead>
                <TableHead className="text-bt-text-tertiary font-mono text-xs uppercase tracking-wider text-right hidden sm:table-cell">7D Chart</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {cryptos.map((c, idx) => {
                const change24 = c.price_change_percentage_24h || 0;
                const isPos = change24 >= 0;
                return (
                  <TableRow key={c.id} className={`border-bt-border-subtle hover:bg-bt-hover transition-colors ${heldBy[c.symbol] ? "bg-bt-gold/5" : ""}`} data-testid={`crypto-row-${c.symbol}`}>
                    <TableCell className="font-mono text-xs text-bt-text-tertiary">{idx + 1}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        {c.image ? (
                          <img src={c.image} alt={c.name} className="w-6 h-6 rounded-full" />
                        ) : (
                          <Coins className="w-6 h-6 text-bt-gold" />
                        )}
                        <div>
                          <a
                            href={`https://www.coingecko.com/en/coins/${c.id}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="font-mono text-sm font-medium text-bt-text-primary hover:text-bt-gold transition-colors"
                            data-testid={`crypto-link-${c.symbol}`}
                          >
                            {c.name}
                          </a>
                          <span className="ml-2 font-mono text-xs text-bt-text-tertiary uppercase">{c.symbol}</span>
                          <HeldBadge sources={heldBy[c.symbol]} symbol={c.symbol} />
                        </div>
                      </div>
                    </TableCell>
                    <TableCell className="text-right font-mono text-sm text-bt-text-primary">
                      ${c.current_price < 0.01 ? c.current_price?.toPrecision(3) : c.current_price?.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: c.current_price < 1 ? 4 : 2 })}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className={`inline-flex items-center gap-1 font-mono text-xs ${isPos ? "text-bt-positive" : "text-bt-negative"}`}>
                        {isPos ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
                        {isPos ? "+" : ""}{change24?.toFixed(2)}%
                      </div>
                    </TableCell>
                    <TableCell className="text-right font-mono text-xs text-bt-text-secondary hidden md:table-cell">
                      {c.market_cap ? (c.market_cap >= 1e12 ? `$${(c.market_cap / 1e12).toFixed(2)}T` : `$${(c.market_cap / 1e9).toFixed(1)}B`) : "—"}
                    </TableCell>
                    <TableCell className="text-right font-mono text-xs text-bt-text-secondary hidden lg:table-cell">
                      {c.total_volume ? `$${(c.total_volume / 1e9).toFixed(1)}B` : "—"}
                    </TableCell>
                    <TableCell className="text-right hidden sm:table-cell">
                      <MiniSparkline data={c.sparkline} positive={isPos} />
                    </TableCell>
                  </TableRow>
                );
              })}
              {extraHeld.map(h => (
                <TableRow key={`extra-${h.symbol}`} className="border-bt-border-subtle bg-bt-gold/5" data-testid={`crypto-row-${h.symbol.toLowerCase()}`}>
                  <TableCell className="font-mono text-xs text-bt-text-tertiary">—</TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      {h.image ? <img src={h.image} alt={h.name} className="w-6 h-6 rounded-full" /> : <Coins className="w-6 h-6 text-bt-gold" />}
                      <span className="font-mono text-sm font-medium text-bt-text-primary">{h.name}</span>
                      <span className="font-mono text-xs text-bt-text-tertiary uppercase">{h.symbol}</span>
                      <HeldBadge sources={heldBy[h.symbol.toLowerCase()]} symbol={h.symbol.toLowerCase()} />
                    </div>
                  </TableCell>
                  <TableCell className="text-right font-mono text-sm text-bt-text-primary">{fmtCryptoPrice(h.price)}</TableCell>
                  <TableCell className={`text-right font-mono text-xs ${(h.change_24h || 0) >= 0 ? "text-bt-positive" : "text-bt-negative"}`}>
                    {h.change_24h != null ? `${h.change_24h >= 0 ? "+" : ""}${Number(h.change_24h).toFixed(2)}%` : "—"}
                  </TableCell>
                  <TableCell className="hidden md:table-cell" /><TableCell className="hidden lg:table-cell" /><TableCell className="hidden sm:table-cell" />
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>
    </div>
  );
}
